import {
  EMPTY_STRING,
  PAYRILLIUM_CONSTANTS,
  PAYRILLIUM_LOG_VENDOR,
  storeConstants,
  payrilliumActions,
} from "../../constants";
import {
  authorizePayment,
  logPayrilliumAction,
  payrilliumAbortTransaction,
  payrilliumCapturePayment,
  refundPayment,
  reverseAuthorization,
  showBasket,
} from "../../services/payrillium.service";
import { setItemToLocalStorage } from "../../system/storage";

// --------------- Transaction Flows ---------------

export const payrilliumProcessSale = async ({
  payment_id,
  amount,
  items = [],
  shouldShowBasket = false,
  cardType = PAYRILLIUM_CONSTANTS.CARD_TYPE.CREDIT,
  tip = PAYRILLIUM_CONSTANTS.TIP.DEFAULT,
  paymentTimeOut = PAYRILLIUM_CONSTANTS.TIMEOUT,
  currentStore = {},
  paymentDevice,
}) => {
  logPayrilliumAction(currentStore, {
    vendorName: PAYRILLIUM_LOG_VENDOR,
    action: payrilliumActions.SALE_INITIATED,
    timestamp: new Date().toISOString(),
    store: { id: currentStore.id, name: currentStore.storeName },
    payment_id,
    amount,
    cardType,
  });

  if (shouldShowBasket && items.length > 0) {
    await showBasket(items, amount).catch(() => {});
  }

  const authResponse = await authorizePayment({
    payment_id,
    amount,
    cardType,
    tip,
    paymentTimeOut,
    signature: EMPTY_STRING,
  });

  if (!authResponse?.success) {
    const errorMsg =
      authResponse?.message ||
      (typeof authResponse?.data?.message === "string"
        ? authResponse.data.message
        : null) ||
      "Payment authorization failed";
    logPayrilliumAction(currentStore, {
      vendorName: PAYRILLIUM_LOG_VENDOR,
      action: payrilliumActions.AUTH_FAILED,
      timestamp: new Date().toISOString(),
      store: { id: currentStore.id, name: currentStore.storeName },
      payment_id,
      amount,
      error: errorMsg,
    });
    throw new Error(errorMsg);
  }

  const authData = authResponse?.data?.message;
  const authTransactionId = authData?.transactionId;

  logPayrilliumAction(currentStore, {
    vendorName: PAYRILLIUM_LOG_VENDOR,
    action: payrilliumActions.AUTH_SUCCESS,
    timestamp: new Date().toISOString(),
    store: { id: currentStore.id, name: currentStore.storeName },
    payment_id,
    amount,
    authTransactionId,
    cardVendor: authData?.cardVendor,
    maskedPan: authData?.cardNumber,
    approvalCode: authData?.approvalCode,
    entryMode: authData?.entryMode,
  });

  if (!authTransactionId) {
    logPayrilliumAction(currentStore, {
      vendorName: PAYRILLIUM_LOG_VENDOR,
      action: payrilliumActions.AUTH_MISSING_TRANSACTION_ID,
      timestamp: new Date().toISOString(),
      store: { id: currentStore.id, name: currentStore.storeName },
      payment_id,
      amount,
    });
    throw new Error("Authorization succeeded but no transactionId returned");
  }

  let captureResponse;
  try {
    captureResponse = await payrilliumCapturePayment({
      payment_id,
      transactionId: authTransactionId,
      amount,
      href: authResponse.data?._links?.capture?.href || EMPTY_STRING,
    });
  } catch (captureError) {
    logPayrilliumAction(currentStore, {
      vendorName: PAYRILLIUM_LOG_VENDOR,
      action: payrilliumActions.CAPTURE_ERROR,
      timestamp: new Date().toISOString(),
      store: { id: currentStore.id, name: currentStore.storeName },
      payment_id,
      amount,
      authTransactionId,
      error: captureError?.message,
    });
    await payrilliumAbortTransaction().catch(() => {});
    logPayrilliumAction(currentStore, {
      vendorName: PAYRILLIUM_LOG_VENDOR,
      action: payrilliumActions.TRANSACTION_ABORTED,
      timestamp: new Date().toISOString(),
      store: { id: currentStore.id, name: currentStore.storeName },
      payment_id,
      reason: "CAPTURE_ERROR",
    });
    await reverseAuthorization({
      payment_id,
      transaction_id: authTransactionId,
      totalAmount: amount,
    }).catch(() => {});
    logPayrilliumAction(currentStore, {
      vendorName: PAYRILLIUM_LOG_VENDOR,
      action: payrilliumActions.AUTH_REVERSED,
      timestamp: new Date().toISOString(),
      store: { id: currentStore.id, name: currentStore.storeName },
      payment_id,
      authTransactionId,
      reason: "CAPTURE_ERROR",
    });
    throw new Error(captureError?.message || "Payment capture failed");
  }

  const captureBody = captureResponse?.data;
  const captureSuccess =
    captureBody?.success ??
    (captureResponse?.status >= 200 && captureResponse?.status < 300);

  if (!captureSuccess) {
    logPayrilliumAction(currentStore, {
      vendorName: PAYRILLIUM_LOG_VENDOR,
      action: payrilliumActions.CAPTURE_FAILED,
      timestamp: new Date().toISOString(),
      store: { id: currentStore.id, name: currentStore.storeName },
      payment_id,
      amount,
      authTransactionId,
      status: captureResponse?.status,
    });
    await payrilliumAbortTransaction().catch(() => {});
    logPayrilliumAction(currentStore, {
      vendorName: PAYRILLIUM_LOG_VENDOR,
      action: payrilliumActions.TRANSACTION_ABORTED,
      timestamp: new Date().toISOString(),
      store: { id: currentStore.id, name: currentStore.storeName },
      payment_id,
      reason: "CAPTURE_FAILED",
    });
    await reverseAuthorization({
      payment_id,
      transaction_id: authTransactionId,
      totalAmount: amount,
    }).catch(() => {});
    logPayrilliumAction(currentStore, {
      vendorName: PAYRILLIUM_LOG_VENDOR,
      action: payrilliumActions.AUTH_REVERSED,
      timestamp: new Date().toISOString(),
      store: { id: currentStore.id, name: currentStore.storeName },
      payment_id,
      authTransactionId,
      reason: "CAPTURE_FAILED",
    });
    throw new Error("Payment capture failed");
  }

  const captureData =
    captureBody?.data?.message || captureBody?.message || captureBody;

  logPayrilliumAction(currentStore, {
    vendorName: PAYRILLIUM_LOG_VENDOR,
    action: payrilliumActions.CAPTURE_SUCCESS,
    timestamp: new Date().toISOString(),
    store: { id: currentStore.id, name: currentStore.storeName },
    payment_id,
    amount,
    authTransactionId,
    captureTransactionId: captureData?.id,
  });

  return {
    creditCardData: {
      paymentBrand: authData?.cardVendor || "Credit",
      maskedPan: authData?.cardNumber || "****",
      paymentTokenValue: captureData?.id,
      approvalCode: authData?.approvalCode || "",
      entryMode: authData?.entryMode || "",
    },
    authTransactionId,
    captureTransactionId: captureData?.id,
  };
};

export const payrilliumProcessRefund = async ({
  payment_id,
  transaction_id,
  amount,
  currentStore = {},
}) => {
  logPayrilliumAction(currentStore, {
    vendorName: PAYRILLIUM_LOG_VENDOR,
    action: payrilliumActions.REFUND_INITIATED,
    timestamp: new Date().toISOString(),
    store: { id: currentStore.id, name: currentStore.storeName },
    payment_id,
    transaction_id,
    amount,
  });

  try {
    const result = await refundPayment({ payment_id, transaction_id, amount });
    logPayrilliumAction(currentStore, {
      vendorName: PAYRILLIUM_LOG_VENDOR,
      action: payrilliumActions.REFUND_SUCCESS,
      timestamp: new Date().toISOString(),
      store: { id: currentStore.id, name: currentStore.storeName },
      payment_id,
      transaction_id,
      amount,
    });
    return result;
  } catch (error) {
    logPayrilliumAction(currentStore, {
      vendorName: PAYRILLIUM_LOG_VENDOR,
      action: payrilliumActions.REFUND_FAILED,
      timestamp: new Date().toISOString(),
      store: { id: currentStore.id, name: currentStore.storeName },
      payment_id,
      transaction_id,
      amount,
      error: error?.message,
    });
    throw error;
  }
};

export const getFormattedPayrilliumBaseUrl = (ip) => {
  return `http://${ip}:6868/payrillium`;
};

export const setPayrilliumBaseUrl = (url, isFormatUrl = true) => {
  let valueToSet = String(url || EMPTY_STRING);
  if (isFormatUrl) {
    valueToSet = getFormattedPayrilliumBaseUrl(url);
  }
  setItemToLocalStorage(
    storeConstants.PAYRILLIUM_BASE_URL_STORAGE_KEY,
    valueToSet
  );
};
